import { useEffect, useRef, useState } from 'react';
import './camera.css';

function ArrowLeftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M19 12H5M11 6l-6 6 6 6" stroke="#3d2f7a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ShutterIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="8" fill="#ffffff" />
    </svg>
  );
}

function RetakeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke="#6c4ce8" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M20 4v5h-5" stroke="#6c4ce8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M5 13l4 4L19 7" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SwitchCameraIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 7h10l-2-2M17 17H7l2 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17 7a7 7 0 0 1 2 5M7 17a7 7 0 0 1-2-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export default function Camera({ onBack, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState('');
  const [facingMode, setFacingMode] = useState('user');

  useEffect(() => {
    let cancelled = false;

    async function startCamera() {
      try {
        setError('');
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        if (!cancelled) {
          setError('Camera access was denied or is unavailable. Please allow camera access and try again.');
        }
      }
    }

    if (!photo) {
      startCamera();
    }

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [facingMode, photo]);

  function switchCamera() {
    setFacingMode((mode) => (mode === 'user' ? 'environment' : 'user'));
  }

  function handleShutter() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    setPhoto(canvas.toDataURL('image/png'));

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }

  function handleRetake() {
    setPhoto(null);
  }

  function handleUsePhoto() {
    onCapture?.(photo);
  }

  return (
    <div className="camera-screen">
      <div className="camera-frame">
        <button type="button" className="camera-back-btn" aria-label="Go back" onClick={onBack}>
          <ArrowLeftIcon />
        </button>

        <div className="camera-viewport">
          {error ? (
            <div className="camera-error">{error}</div>
          ) : photo ? (
            <img src={photo} alt="Your selfie" className="camera-preview-img" />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={'camera-video' + (facingMode === 'user' ? ' camera-video-front' : '')}
            />
          )}
        </div>

        {!error && (
          <div className="camera-actions">
            {photo ? (
              <>
                <button type="button" className="camera-btn-secondary" onClick={handleRetake}>
                  <span className="camera-btn-icon">
                    <RetakeIcon />
                  </span>
                  Retake
                </button>
                <button type="button" className="camera-btn-primary" onClick={handleUsePhoto}>
                  <span className="camera-btn-icon">
                    <CheckIcon />
                  </span>
                  Use Photo
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="camera-switch-btn"
                  aria-label="Switch camera"
                  onClick={switchCamera}
                >
                  <SwitchCameraIcon />
                </button>
                <button
                  type="button"
                  className="camera-shutter-btn"
                  aria-label="Take photo"
                  onClick={handleShutter}
                >
                  <ShutterIcon />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
